import React, { ReactElement, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import format from 'date-fns/format';
import { Badge, Button, Hint, Input } from '@bucketplace/commerce-admin-ui';
import { Box, Divider, Flex, Text, useSnackbar } from '@bucketplace/design-system';

import { useRaffleContext } from '@/contexts';
import { DocumentInspectorProps, DocumentNode } from '@/types';

import { FormContent, InspectorFormProvider, LogDataFormGroup } from '../../components';
import { RaffleProps } from './Raffle';
import { RAFFLE_EVENT_STATUS_LOOKUP, RAFFLE_GROUP_STATUS, RAFFLE_GROUP_STATUS_LOOKUP, RaffleGroupDetail } from './types';

const formatDateTime = (isoString: string): string => {
  return format(new Date(isoString), 'yyyy-MM-dd HH:mm');
};

const sortRaffleEventsByStartDate = (
  groupDetail: RaffleGroupDetail,
): RaffleGroupDetail => {
  const { events } = groupDetail;
  if (events.length === 0) {
    return groupDetail;
  }

  return {
    ...groupDetail,
    events: [...events].sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()),
  };
};

export function RaffleInspector({
  value,
  onChange,
}: DocumentInspectorProps<RaffleProps>): ReactElement {
  return (
    <InspectorFormProvider value={value} onChange={onChange}>
      <RaffleInspectorContent value={value} onChange={onChange} />
    </InspectorFormProvider>
  );
}

function RaffleInspectorContent({
  value,
  onChange,
}: DocumentInspectorProps<RaffleProps>): ReactElement {
  const { open } = useSnackbar();

  const { getRaffleGroup, setLogData } = useRaffleContext();
  const { children, ...others } = value ?? {};

  const [inputValue, setInputValue] = useState(value?.props?.groupId || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [raffleGroupDetail, setRaffleGroupDetail] = useState<RaffleGroupDetail | null>(null);

  const handleRegister = async (): Promise<void> => {
    const trimmedGroupId = inputValue?.trim();

    if (!trimmedGroupId) {
      setErrorMessage('래플 그룹 ID를 입력해주세요.');
      return;
    }

    try {
      const raffleGroupDetail = await getRaffleGroup?.(trimmedGroupId);

      if (!raffleGroupDetail || raffleGroupDetail.group.id == null && raffleGroupDetail.group.status !== 'ACTIVATE') {
        setErrorMessage('유효하지 않은 래플 그룹 ID입니다.');
        return;
      }

      if (raffleGroupDetail.group.status !== RAFFLE_GROUP_STATUS.ACTIVATE) {
        open({ content: '이미 종료된 래플 그룹입니다.' });
        return;
      }

      // 검증 성공 - onChange 호출
      setErrorMessage('');
      setRaffleGroupDetail(sortRaffleEventsByStartDate(raffleGroupDetail));
      const newRaffleNode: DocumentNode<RaffleProps> = {
        ...value,
        props: {
          ...value.props,
          groupId: trimmedGroupId,
        },
      };
      onChange(newRaffleNode);
    } catch (err) {
      const message = (err as Error).message ?? '래플 그룹을 조회할 수 없습니다.';
      setErrorMessage(message);
    }
  };

  useEffect(() => {
    const initialGroupId = value?.props?.groupId;
    if (initialGroupId && getRaffleGroup) {
      const loadRaffleGroup = async (): Promise<void> => {
        const detail = await getRaffleGroup(initialGroupId);
        setRaffleGroupDetail(sortRaffleEventsByStartDate(detail));
      };
      loadRaffleGroup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <LogDataFormGroup nodeKey={value?.key} {...setLogData?.(others)} />
      <FormContent
        header="래플 그룹 ID"
        direction="VERTICAL"
      >
        <Input
          size={32}
          placeholder="래플 그룹 ID를 입력해주세요."
          value={inputValue}
          onChange={(e) => {
            const newValue = e.target.value.replace(/\D/g, '');
            setInputValue(newValue);
            setErrorMessage('');
          }}
        />
        { errorMessage && (
          <Hint className="hint" error>
            { errorMessage }
          </Hint>
        ) }
      </FormContent>
      <div css={css`
        padding: 16px;
      `}
      >
        <Button
          size={32}
          onClick={handleRegister}
          full
        >
          등록
        </Button>
      </div>
      { raffleGroupDetail && (
        <Box>
          <FormContent header="래플 그룹" direction="VERTICAL">
            <Flex direction="row" justify="space-between" align="center">
              <Text variant="body14L18">
                { raffleGroupDetail?.group.name }
              </Text>
              <Badge badgeText={RAFFLE_GROUP_STATUS_LOOKUP[raffleGroupDetail?.group.status]} css={{ minWidth: 'fit-content' }} />
            </Flex>
          </FormContent>
          <FormContent header="래플 이벤트" direction="VERTICAL">
            <ul>
              { raffleGroupDetail?.events.map((item, index) => {
                return (
                  <ListItem>
                    <Flex gap={6}>
                      <Flex direction="row" justify="space-between" align="center">
                        <Flex direction="row" align="center" justify="center" gap={8}>
                          <Text variant="body14L18">{ `이벤트 ${index + 1} ` }</Text>
                          <Text variant="body14L18" color="foregroundBrand">{ `#${item.id}` }</Text>
                        </Flex>
                        <Badge badgeText={RAFFLE_EVENT_STATUS_LOOKUP[item.status]} />
                      </Flex>
                      <Flex direction="row" gap={8}>
                        <Text variant="body14L18" color="foregroundWeak">시작 일정</Text>
                        <Text variant="body14L18" color="foreground">{ formatDateTime(item.startAt) }</Text>
                      </Flex>
                      <Flex direction="row" gap={8} mb={6}>
                        <Text variant="body14L18" color="foregroundWeak">종료 일정</Text>
                        <Text variant="body14L18" color="foreground">{ formatDateTime(item.endAt) }</Text>
                      </Flex>
                      { index !== raffleGroupDetail?.events.length - 1 && (
                        <Divider height={1} />
                      ) }
                    </Flex>
                  </ListItem>
                );
              }) }
            </ul>
          </FormContent>
        </Box>
      ) }
    </>
  );
}

const ListItem = styled.li`
  position: relative;
  padding: 8px 0;
`;
